const { Type } = require('@sinclair/typebox');
const Blockly = require('blockly');
const { merge } = require('lodash');
const setupAddCodeProperties = require('../../../../helpers/addCodeProperties');
const { moduleRegistration } = require('@sstdev/lib_metadata-config');

const TOOLTIP = 'A nesting element that allows for filtering based on nested properties.';

/**
 * @param {*} color
 * @param {import('blockly').Block} blocklyBlock
 */
function addBlockSpecificFields(color, blocklyBlock, mdcBlock, line1OfInputs) {
    blocklyBlock.setTooltip(TOOLTIP);
    blocklyBlock.setInputsInline(true);

    line1OfInputs.appendField('  Property Name:').appendField(new Blockly.FieldTextInput('title'), 'propertyName');

    //Statement inputs ALWAYS go on their own line.
    //line 2:
    blocklyBlock
        .appendStatementInput('NestedObjectElements')
        .setCheck(['searchElement'])
        .appendField('Nested Search Elements:');

    //line 3, just text
    blocklyBlock.appendDummyInput().setAlign(Blockly.ALIGN_RIGHT).appendField('But only where it also matches');

    //line 4, just the filters input gap: (by definition on a new line)
    blocklyBlock.appendStatementInput('filters').setCheck(['filter']).appendField('All of these filters (Optional):');
}

/**
 * @param {{[key: string]: any}} code
 * @param {import('blockly').Block} blocklyBlock
 * @return {{[key: string]: any}}
 */
function convertCustomFieldsToCode(code, blocklyBlock) {
    const addCodeProperties = setupAddCodeProperties(code, blocklyBlock);
    const propertyName = blocklyBlock.getFieldValue('propertyName');

    // Nested Search elements are automatically added
    addCodeProperties([{ key: 'propertyName', handler: () => propertyName }]);

    code.filters = JSON.parse(`[${Blockly.JavaScript.statementToCode(blocklyBlock, 'filters')}]`);

    return code;
}

/** Builds the data dictionary fragment for the NestedDetail search element. */
function asDataDictionary(hNode, context) {
    const { propertyName } = hNode;
    //first let's look at nested search elements:
    //Each child returns TypeBox schemas in their partialTypeDefinition
    let [childDefinedSubDataDictionary, childDefinedPartialTypeDefinition] = hNode.children.reduce(
        (soFar, child) => {
            const [childSubDD = {}, childPartial = {}] =
                moduleRegistration
                    .getHNodeType(child.hNodeTypeGroup, child.hNodeType)
                    ?.asDataDictionary?.(child, context) || [];
            return [merge(soFar[0], childSubDD), Object.assign(soFar[1], childPartial)];
        },
        [{}, {}]
    );

    //now let's also look at filters if they exist:
    if (hNode.filters?.length) {
        [childDefinedSubDataDictionary, childDefinedPartialTypeDefinition] = hNode.filters?.reduce(
            (soFar, child) => {
                const [childSubDD = {}, childPartial = {}] =
                    moduleRegistration.getHNodeType('filters', child.hNodeType)?.asDataDictionary?.(child, context) ||
                    [];
                return [merge(soFar[0], childSubDD), Object.assign(soFar[1], childPartial)];
            },
            [childDefinedSubDataDictionary, childDefinedPartialTypeDefinition]
        );
    }

    const isArray = Array.isArray(hNode.filters) && hNode.filters.length > 0;
    const childObjectSchema = Type.Object(childDefinedPartialTypeDefinition, {
        title: 'Item',
        'x-dataType': 'object',
        additionalProperties: true
    });
    const propertySchema = isArray
        ? Type.Array(childObjectSchema, {
              'x-dataType': 'array',
              'x-propertyName': propertyName
          })
        : Type.Object(childDefinedPartialTypeDefinition, {
              'x-dataType': 'object',
              'x-propertyName': propertyName,
              additionalProperties: true
          });

    const partialTypeDefinition = {
        [propertyName]: propertySchema
    };

    //And the given foreignKey exists on the parent object.
    return [childDefinedSubDataDictionary, partialTypeDefinition];
}

module.exports = { addBlockSpecificFields, convertCustomFieldsToCode, asDataDictionary };
